import { errorToast } from "@/components/ui/toast.tsx";
import { apiErrorMessage, apiErrorReason } from "@/lib/http.ts";
import { CHECKOUT_ALREADY_SUBSCRIBED_REASON, type PlanResp } from "@/types/billing.ts";
import type { PaymentMethodListResp } from "@/types/wallet.ts";
import { PAYMENT_METHOD_DELETE_BLOCKED_REASON } from "@/types/wallet.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cancelPlan,
  changePlan,
  deletePaymentMethod,
  getPaymentMethods,
  getPlan,
  keepCurrentPlan,
  refreshCheckout,
  refreshPaymentMethods,
  resumePlan,
  setDefaultPaymentMethod,
  startCheckout,
  type PlanChoice,
} from "./billingApi.ts";

const QUERY_KEY = ["paymentMethods"] as const;
const PLAN_QUERY_KEY = ["billingPlan"] as const;

/** True when the server refused a delete pending the last-card warning. */
export function isLastMethodBlocked(err: unknown): boolean {
  return apiErrorReason(err) === PAYMENT_METHOD_DELETE_BLOCKED_REASON;
}

export const usePaymentMethods = () => {
  return useQuery<PaymentMethodListResp>({
    queryKey: QUERY_KEY,
    queryFn: getPaymentMethods,
  });
};

export const useRefreshPaymentMethods = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: refreshPaymentMethods,
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data);
    },
    onError: () => {
      errorToast("Couldn't refresh your saved cards");
    },
  });
};

/**
 * Deleting is two-phase by design: the caller retries with `confirm` after
 * showing the warning, so a rejection carrying the blocked reason is an
 * expected outcome rather than a failure to report.
 */
export const useDeletePaymentMethod = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, confirm }: { id: string; confirm?: boolean }) =>
      deletePaymentMethod(id, confirm ?? false),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) => {
      if (isLastMethodBlocked(err)) return;
      errorToast("Couldn't remove that card");
    },
  });
};

export const useSetDefaultPaymentMethod = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => setDefaultPaymentMethod(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: () => {
      errorToast("Couldn't change your default card");
    },
  });
};

export const usePlan = () => {
  return useQuery<PlanResp>({
    queryKey: PLAN_QUERY_KEY,
    queryFn: getPlan,
  });
};

/** True when the server refused a checkout because a plan is already active. */
export function isAlreadySubscribed(err: unknown): boolean {
  return apiErrorReason(err) === CHECKOUT_ALREADY_SUBSCRIBED_REASON;
}

/**
 * Starts a purchase. Success navigates away to Stripe rather than resolving
 * into the UI, so there is no success toast — the next thing the user sees is
 * Checkout.
 */
export const useStartCheckout = () => {
  return useMutation({
    mutationFn: (choice: PlanChoice) => startCheckout(choice),
    onSuccess: (data) => {
      // A full document navigation, not a router push: the destination is
      // Stripe's own page and not part of this app.
      window.location.assign(data.url);
    },
    onError: (err) => {
      errorToast(checkoutFailureMessage(err));
    },
  });
};

/**
 * What to tell someone whose checkout did not start: the server's own
 * sentence for a refusal it explains (a plan not sold on that interval).
 * A plan not on sale in this environment comes back as a 503, whose body is
 * deliberately not read, so it gets the generic retry wording.
 */
function checkoutFailureMessage(err: unknown): string {
  if (isAlreadySubscribed(err)) return "You already have an active plan";
  return apiErrorMessage(err) ?? "Couldn't start checkout. Please try again.";
}

/**
 * Settles the plan when Stripe returns the user to the app. The saved-cards
 * list is invalidated too, because paying for a subscription also vaults the
 * card that paid for it.
 */
export const useCompleteCheckout = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => refreshCheckout(sessionId),
    onSuccess: async (data) => {
      qc.setQueryData(PLAN_QUERY_KEY, data);
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: () => {
      // The purchase itself may well have succeeded — only this read failed —
      // so the copy must not tell the user their payment did not go through.
      errorToast("Your payment went through, but we couldn't refresh this page. Reload to check.");
    },
  });
};

/**
 * Plan mutations all answer with the settled plan, so on success each writes
 * the query cache directly instead of re-fetching what the server just
 * computed. A failure re-reads the plan, since a refused change can still have
 * altered it.
 */
function usePlanMutation<TArg>(
  mutationFn: (arg: TArg) => Promise<PlanResp>,
  { failure }: { failure: string },
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (plan) => {
      qc.setQueryData(PLAN_QUERY_KEY, plan);
    },
    onError: async (err) => {
      // A refused change says why in its own words — a payment that didn't go
      // through, a scheduled change it cancelled — which a generic sentence
      // would hide. The plan is re-read either way, since a held change can
      // still have released a scheduled one.
      errorToast(apiErrorMessage(err) ?? failure);
      await qc.invalidateQueries({ queryKey: PLAN_QUERY_KEY });
    },
  });
}

export const useChangePlan = () =>
  usePlanMutation((choice: PlanChoice) => changePlan(choice), {
    failure: "Couldn't change your plan",
  });

export const useCancelPlan = () =>
  usePlanMutation<void>(() => cancelPlan(), {
    failure: "Couldn't cancel your plan",
  });

export const useResumePlan = () =>
  usePlanMutation<void>(() => resumePlan(), {
    failure: "Couldn't resume your plan",
  });

export const useKeepCurrentPlan = () =>
  usePlanMutation<void>(() => keepCurrentPlan(), {
    failure: "Couldn't keep your current plan",
  });
